import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

// --- localStorage Cache Layer ---
const CACHE_KEY = 'ts_listings_cache_v7_slim';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
const FIRST_BATCH_SIZE = 24;
const BACKGROUND_BATCH_SIZE = 200;

const CARD_COLUMNS = `
  id,
  title,
  status,
  year,
  make,
  model,
  trim,
  price,
  original_price,
  discount_percent,
  mileage,
  location,
  location_th,
  import_type,
  fuel_type,
  transmission,
  color,
  engine,
  interior_color,
  drivetrain,
  image_urls,
  verified,
  promo,
  engagement_score,
  damage_status,
  owner_count,
  document_status,
  finance_status,
  warranty_status,
  transfer_status,
  dealer,
  notes,
  notes_th,
  days_on_market,
  views,
  listing_code
`;

interface CacheEntry {
  listings: any[];
  timestamp: number;
  sortBy: string;
}

function readCache(sortBy: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (entry.sortBy !== sortBy) return null;
    return entry;
  } catch {
    return null;
  }
}

function writeCache(listings: any[], sortBy: string): void {
  // Serializing 200 rows is expensive — never do it on the critical path.
  const run = () => {
    try {
      const entry: CacheEntry = { listings, timestamp: Date.now(), sortBy };
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
      // localStorage full — silently ignore
    }
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as any).requestIdleCallback(run, { timeout: 3000 });
  } else {
    globalThis.setTimeout(run, 1500);
  }
}

function isCacheFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp < CACHE_TTL;
}

// Keep the previous object for rows whose data did not change so React can skip
// re-rendering those cards. Comparing a few cheap fields instead of
// JSON.stringify keeps this off the critical path for 200+ rows.
function sameRow(a: any, b: any): boolean {
  return (
    a.price === b.price &&
    a.status === b.status &&
    a.mileage === b.mileage &&
    a.promo === b.promo &&
    a.engagement_score === b.engagement_score &&
    a.image_urls?.length === b.image_urls?.length &&
    a.image_urls?.[0] === b.image_urls?.[0]
  );
}

function mergeStable(prev: any[], next: any[]): any[] {
  if (!prev.length) return next;
  const prevById = new Map(prev.map((row) => [row.id, row]));
  let changed = prev.length !== next.length;
  const merged = next.map((row, i) => {
    const old = prevById.get(row.id);
    if (old && sameRow(old, row)) {
      if (prev[i]?.id !== row.id) changed = true;
      return old;
    }
    changed = true;
    return row;
  });
  return changed ? merged : prev;
}

function waitForBackgroundSlot(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => resolve(), { timeout: 1200 });
    } else {
      globalThis.setTimeout(resolve, 600);
    }
  });
}

function applySort(query: any, sortBy: string) {
  if (sortBy === 'price-low') {
    return query.order('price', { ascending: true });
  }
  if (sortBy === 'price-high') {
    return query.order('price', { ascending: false });
  }
  if (sortBy === 'mileage-low') {
    return query.order('mileage', { ascending: true });
  }
  if (sortBy === 'mileage-high') {
    return query.order('mileage', { ascending: false });
  }
  if (sortBy === 'year-new') {
    return query.order('year', { ascending: false });
  }
  if (sortBy === 'year-old') {
    return query.order('year', { ascending: true });
  }
  if (sortBy === 'popular') {
    return query.order('engagement_score', { ascending: false, nullsFirst: false });
  }
  return query.order('sort_order_default', { ascending: true, nullsFirst: false });
}

function formatListing(row: any, options: { includeFullImages?: boolean } = {}) {
  const fullImages = Array.isArray(row.image_urls) ? row.image_urls.filter(Boolean) : [];
  const cardImages = row.image_url ? [row.image_url] : fullImages.slice(0, 1);
  // Cap carousel/Spotlight images: 6 is plenty and keeps payload + cache small.
  const imageUrls = options.includeFullImages
    ? (fullImages.length > 0 ? fullImages.slice(0, 6) : cardImages)
    : cardImages;

  return {
    id: row.id,
    title: row.title || `${row.year} ${row.make} ${row.model}`,
    status: row.status || 'LIVE',
    year: row.year,
    make: row.make,
    model: row.model,
    trim: row.trim,
    price: row.price,
    price_thb: row.price,
    original_price: row.original_price,
    old_price_thb: row.original_price,
    discount_price_thb: row.original_price ? row.price : null,
    discount_percent: row.discount_percent,
    mileage: row.mileage,
    mileage_km: row.mileage,
    location: row.location,
    district: row.location,
    location_th: row.location_th,
    district_th: row.location_th,
    import_type: row.import_type,
    fuel_type: row.fuel_type,
    transmission: row.transmission,
    color: row.color,
    engine: row.engine,
    interior_color: row.interior_color,
    drivetrain: row.drivetrain,
    image_url: row.image_url || imageUrls[0] || null,
    image_urls: imageUrls,
    full_image_count: options.includeFullImages ? imageUrls.length : undefined,
    motorverified: row.verified,
    verified: row.verified,
    promo_active: row.promo,
    promo: row.promo,
    engagement_score: row.engagement_score,
    damage_status: row.damage_status || 'UNKNOWN',
    owner_count: row.owner_count || 'UNKNOWN',
    document_status: row.document_status || 'UNKNOWN',
    finance_status: row.finance_status || 'UNKNOWN',
    warranty_status: row.warranty_status || 'UNKNOWN',
    transfer_status: row.transfer_status || 'UNKNOWN',
    dealer: row.dealer,
    notes: row.notes,
    notes_th: row.notes_th,
    days_on_market: row.days_on_market,
    views: row.views,
    listing_code: row.listing_code,
  };
}

export async function fetchListingDetails(id: string) {
  const { data, error } = await supabase
    .from('cached_listings')
    .select(CARD_COLUMNS)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data ? formatListing(data, { includeFullImages: true }) : null;
}

// --- Hook ---

interface UseInfiniteListingsOptions {
  enabled?: boolean;
  sortBy?: string;
}

export function useInfiniteListings(options: UseInfiniteListingsOptions = {}) {
  const { enabled = true, sortBy = 'default' } = options;
  
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const isFetchingRef = useRef(false);

  const fetchAll = useCallback(async (showLoading: boolean) => {
    if (!enabled || isFetchingRef.current) return;

    isFetchingRef.current = true;
    if (showLoading) setLoading(true);
    setError(null);

    try {
      // Stage 1: direct Supabase read for fast first paint. This uses cached_listings
      // (not Airtable and not an Edge Function) and only pulls the primary card image.
      const firstQuery = applySort(
        supabase.from('cached_listings').select(CARD_COLUMNS),
        sortBy
      ).range(0, FIRST_BATCH_SIZE - 1);
      const { data: firstData, error: firstError } = await firstQuery;
      if (firstError) throw firstError;

      const firstBatch = (firstData || []).map((row) => formatListing(row, { includeFullImages: true }));
      if (showLoading) {
        setListings((prev) => mergeStable(prev, firstBatch));
        setLoading(false);
      }

      await waitForBackgroundSlot();

      // Stage 2: fetch the rest in the background with all images so Spotlight can swipe through them.
      const restQuery = applySort(
        supabase.from('cached_listings').select(CARD_COLUMNS),
        sortBy
      ).range(0, BACKGROUND_BATCH_SIZE - 1);
      const { data: restData, error: restError } = await restQuery;
      if (restError) throw restError;

      const fullList = restData?.length ? restData.map((row) => formatListing(row, { includeFullImages: true })) : firstBatch;
      setListings((prev) => mergeStable(prev, fullList));
      writeCache(fullList, sortBy);
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [enabled, sortBy]);

  // Fetch on mount and when sortBy changes
  useEffect(() => {
    isFetchingRef.current = false;

    // Check cache first for instant paint, but always revalidate in background.
    // A bad cached listing/image order can otherwise persist for the full TTL
    // and survive code reverts in the user's browser.
    const cached = readCache(sortBy);
    if (cached) {
      setListings(cached.listings);
      setLoading(false);

      // Fresh cache paints instantly; revalidate later so it does not compete with LCP images.
      if (isCacheFresh(cached)) {
        waitForBackgroundSlot().then(() => fetchAll(false));
      } else {
        fetchAll(false);
      }
      return;
    }

    setListings([]);
    fetchAll(true);
  }, [sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the same API shape for backwards compat
  const reset = useCallback(() => {
    setListings([]);
    fetchAll(true);
  }, [fetchAll]);

  return {
    listings,
    loading,
    loadingMore: false,
    error,
    reset,
    hasMore: false,
    fetchMore: () => {},
    totalCount: listings.length
  };
}
